import React, { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import api from "../api";
import "./ProfilePage.css";
import { User } from "../types/User";
import { FaCamera, FaPlus, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { SERVER_URL } from "../config";
import PhonePromptModal from "../components/PhonePromptModal";

import { Capacitor } from "@capacitor/core";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";

const MAX_LINKS = 5;
type Currency = "usd" | "eur";

const currencySymbol = (c: string) => (c.toLowerCase() === "eur" ? "€" : "$");
const sanitizeInsta = (v: string) =>
  v.replace(/^@+/, "").replace(/[^a-zA-Z0-9._]/g, "").slice(0, 30);
const isNonEmpty = (s: string) => s.trim().length > 0;

const normalizeUrl = (v: string) => {
  const s = v.trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  if (/^[a-z]+:\/\//i.test(s)) return s;
  return `https://${s}`;
};

const toAbs = (url?: string | null) =>
  url && !url.startsWith("http") ? `${SERVER_URL}${url}` : (url ?? "");

const ProfilePage: React.FC = () => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const [profile, setProfile] = useState<User | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [currency, setCurrency] = useState<Currency>("usd");

  // SMS states
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isSmsEnabled, setIsSmsEnabled] = useState(false);

  // Phone prompt modal state
  const [showPhonePrompt, setShowPhonePrompt] = useState(false);

  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const navigate = useNavigate();
  const [delOpen, setDelOpen] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [deletionInfo, setDeletionInfo] = useState<{
    status?: "active" | "pending_delete" | "deleted";
    scheduled?: string | null;
  } | null>(null);

  const [instagram, setInstagram] = useState<string>("");
  const [bio, setBio] = useState<string>("");
  const [links, setLinks] = useState<string[]>([]);
  const [message, setMessage] = useState("");

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    api.get(`/users/${user.slug}`).then((res) => {
      const data = res.data as User & {
        links?: string[];
        instagram?: string;
        avatarUrl?: string;
        earnedByCurrency?: Record<string, { gross: number; net: number; count: number }>;
        earnedAmount?: number;
        acceptedCount?: number;
      };

      setProfile(data);
      setDeletionInfo({
        status: (data as any).status || "active",
        scheduled: (data as any).deletionScheduledFor || null,
      });
      setName(data.name);
      setEmail(data.email);
      setAvatarPreview(toAbs(data.avatarUrl) || null);
      setCurrency((data.currency || "usd") as Currency);
      setInstagram((data.instagram || "").replace(/^@+/, ""));
      setBio(data.bio || "");

      // Initialize phone and notification status
      setPhoneNumber(data.phoneNumber || "");
      setIsSmsEnabled(!!data.isSmsEnabled);

      const arr = Array.isArray(data.links) ? data.links.slice(0, MAX_LINKS) : [];
      setLinks(arr);

      // Show phone prompt if user has no phone number
      if (!data.phoneNumber) {
        setShowPhonePrompt(true);
      }
    });
  }, [user, isAuthenticated]);

  // Логика: если номер пустой, автоматически выключаем уведомления
  useEffect(() => {
    if (!phoneNumber.trim()) {
      setIsSmsEnabled(false);
    }
  }, [phoneNumber]);

  const uploadAvatarBlob = async (blob: Blob, filename = "avatar.jpg") => {
    if (!user) return;

    const tempUrl = URL.createObjectURL(blob);
    setAvatarPreview(tempUrl);

    const formData = new FormData();
    formData.append("avatar", blob, filename);

    try {
      const res = await api.put(`/users/${user.slug}`, formData);
      setProfile(res.data);
      setAvatarPreview(toAbs(res.data.avatarUrl) || tempUrl);
    } catch (err) {
      console.error("Upload failed:", err);
      try {
        const refreshed = await api.get<User>(`/users/${user.slug}`);
        setProfile(refreshed.data);
        setAvatarPreview(toAbs(refreshed.data.avatarUrl) || null);
      } catch {}
    } finally {
      URL.revokeObjectURL(tempUrl);
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setAvatarFile(file);
    await uploadAvatarBlob(file, file.name || "avatar.jpg");
    setAvatarFile(null);
  };

  const handleAvatarClick = async () => {
    const isNative = Capacitor.isNativePlatform();
    if (!isNative) {
      fileInputRef.current?.click();
      return;
    }

    try {
      // Форсим JPEG и меньший размер для совместимости с сервером (HEIC/Web huge size)
      const photo = await Camera.getPhoto({
        quality: 70,
        allowEditing: false,
        source: CameraSource.Prompt,
        resultType: CameraResultType.Base64,
        presentationStyle: "popover",
        saveToGallery: false,
        webUseInput: true,
      });

      if (!photo?.base64String) return;
      const dataUrl = `data:image/jpeg;base64,${photo.base64String}`;
      const resp = await fetch(dataUrl);
      const blob = await resp.blob();

      await uploadAvatarBlob(blob, "avatar.jpg");
    } catch (err) {
      console.log("Camera canceled or failed:", err);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!user) return;
    try {
      const res = await api.put(`/users/${user.slug}`, { avatarUrl: null });
      setProfile(res.data);
      setAvatarPreview(null);
    } catch (err) {
      console.error("Failed to remove avatar:", err);
    }
  };

  // ===== Links handlers =====
  const handleAddLink = () => {
    setLinks((prev) => {
      if (prev.length >= MAX_LINKS) return prev;
      return [...prev, ""];
    });
  };

  const handleChangeLink = (index: number, value: string) => {
    setLinks((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleRemoveLink = (index: number) => {
    setLinks((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  };

  const requestAccountDeletion = async () => {
    if (!user) return;
    try {
      setDelBusy(true);
      const res = await api.post(`/users/${user.slug}/delete`);
      const scheduled = res.data?.deletionScheduledFor;
      setDeletionInfo({ status: "pending_delete", scheduled });
      setDelOpen(false);
      alert("Your account is scheduled to be deleted in 7 days. Log in anytime within this period to cancel.");
      await api.post("/auth/logout");
      delete api.defaults.headers.common.Authorization;
      navigate("/login", { replace: true });
    } catch (e) {
      console.error(e);
      alert("Failed to schedule deletion. Please try again.");
    } finally {
      setDelBusy(false);
    }
  };

  const cancelAccountDeletion = async () => {
    if (!user) return;
    try {
      setDelBusy(true);
      await api.post(`/users/${user.slug}/cancel-deletion`);
      setDeletionInfo({ status: "active", scheduled: null });
      alert("Deletion was canceled. Your account remains active.");
    } catch (e) {
      console.error(e);
      alert("Failed to cancel deletion. Please try again.");
    } finally {
      setDelBusy(false);
    }
  };

  const handleSave = async () => {
    if (!user || !profile) return;

    setMessage("");

    const formData = new FormData();
    formData.append("name", name);
    formData.append("email", email);
    formData.append("instagram", sanitizeInsta(instagram));
    formData.append("bio", bio.slice(0, 80));

    // Добавляем телефон и статус уведомлений
    formData.append("phoneNumber", phoneNumber);
    formData.append("isSmsEnabled", String(isSmsEnabled));

    const cleaned = links.map(normalizeUrl).filter(isNonEmpty).slice(0, MAX_LINKS);
    formData.append("links", JSON.stringify(cleaned));

    if (avatarFile) formData.append("avatar", avatarFile);

    try {
      await api.put(`/users/${user.slug}`, formData);

      if ((profile.currency || "usd") !== currency) {
        await api.patch(`/users/${user.slug}/currency`, { currency });
      }

      const refreshed = await api.get<User>(`/users/${user.slug}`);
      setProfile(refreshed.data);
      setMessage("Profile updated successfully.");
      if (refreshed.data.avatarUrl) {
        setAvatarPreview(toAbs(refreshed.data.avatarUrl));
        setAvatarFile(null);
      }
    } catch (e) {
      console.error(e);
      setMessage("Failed to update profile.");
    }
  };

  if (!profile) return <p style={{ textAlign: "center" }}>Loading...</p>;

  const defaultAvatarPath = "/default_avatar.jpg";
  const avatarSrc = avatarPreview ? toAbs(avatarPreview) : defaultAvatarPath;
  const canAddMore = links.length < MAX_LINKS;

  return (
    <div className="profile-container">
      <h2 className="profile-title">My Profile</h2>

      <div className="profile-avatar-section">
        <div className="profile-avatar-wrapper">
          <img
            src={avatarSrc}
            alt="Avatar"
            className="profile-avatar-image"
            onError={(e) => (e.currentTarget.src = defaultAvatarPath)}
          />

          <button
            type="button"
            className="avatar-upload-label"
            title="Upload avatar"
            onClick={handleAvatarClick}
          >
            <FaCamera className="camera-icon" size={20} />
          </button>

          {/* скрытый input — используется только на Web как фолбэк */}
          <input
            ref={fileInputRef}
            type="file"
            id="avatar"
            accept="image/*"
            onChange={handleAvatarChange}
            style={{ display: "none" }}
          />

          {avatarPreview && (
            <button
              className="avatar-remove-button"
              onClick={handleRemoveAvatar}
              title="Remove avatar"
            >
              ×
            </button>
          )}
        </div>

        <div className="user-info">
          <h2>{profile.name}</h2>
          <p>{profile.email}</p>
          <p style={{ marginTop: 6, fontSize: "0.82rem", color: "#9ca3af" }}>
            Earnings:{" "}
            {profile.earnedByCurrency && Object.keys(profile.earnedByCurrency).length > 0 ? (
              Object.entries(profile.earnedByCurrency).map(([cur, v], i) => (
                <span key={cur}>
                  {i > 0 && " / "}
                  <span className="lifetime-profil">
                    {currencySymbol(cur)}{v?.gross?.toFixed?.(2) ?? "0.00"}
                  </span>
                </span>
              ))
            ) : (
              <span className="lifetime-profil">
                {currencySymbol(profile.currency || currency)}{(profile as any).earnedAmount?.toFixed?.(2) ?? "0.00"}
              </span>
            )}
            {" "}·{" "}
            <span className="lifetime-profil">{(profile as any).acceptedCount ?? 0}</span> accepted
          </p>
        </div>
      </div>

      <button
        type="button"
        className="view-page-btn"
        onClick={() => navigate(`/${profile.slug}`)}
      >
        View my Page
      </button>

      <div className="profile-form">
        <label>Artist Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} />

        <label>Bio</label>
        <div className="bio-input-wrapper">
          <input
            placeholder="What's your vibe? Keep it short & fun"
            value={bio}
            onChange={(e) => setBio(e.target.value.slice(0, 80))}
            maxLength={80}
          />
          <span className="bio-char-count">{bio.length}/80</span>
        </div>

        <label>Currency</label>
        <select
          className="profile-select"
          value={currency}
          onChange={(e) => setCurrency(e.target.value as Currency)}
        >
          <option value="usd">USD ($)</option>
          <option value="eur">EUR (€)</option>
        </select>

        <label>
          Instagram {instagram.trim() && <span className="verified-check" title="Connected">&#10003;</span>}
        </label>
        <div className="instagram-input-wrapper fixed-at">
          <span className="instagram-prefix" aria-hidden="true">
            @
          </span>
          <input
            className="instagram-input"
            placeholder="username"
            value={instagram}
            onChange={(e) => setInstagram(sanitizeInsta(e.target.value))}
            inputMode="text"
            autoComplete="off"
          />
        </div>

        {/* --- SMS Notification Section --- */}
        <div style={{ marginTop: "0.25rem", marginBottom: "0.5rem" }}>
          <label style={{ display: "block", marginBottom: "0.5rem" }}>
            Phone Number (Optional) {phoneNumber.trim() && <span className="verified-check" title="Added">&#10003;</span>}
          </label>
          <input
            className="profile-select" /* Используем тот же стиль, что и у других input */
            style={{ width: "100%", boxSizing: "border-box" }}
            type="tel"
            placeholder="+1234567890"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />

          <div 
            style={{ 
              marginTop: "0.75rem", 
              display: "flex", 
              alignItems: "center", 
              gap: "10px",
              opacity: phoneNumber ? 1 : 0.5,
              cursor: phoneNumber ? "pointer" : "not-allowed"
            }}
          >
            <input
              id="sms-check"
              type="checkbox"
              checked={isSmsEnabled}
              onChange={(e) => setIsSmsEnabled(e.target.checked)}
              disabled={!phoneNumber}
              style={{ width: "18px", height: "18px", cursor: "inherit" }}
            />
            <label 
              htmlFor="sms-check" 
              style={{ cursor: "inherit", fontWeight: "normal", fontSize: "0.95rem" }}
            >
              Activate SMS notifications
            </label>
          </div>
          
          {!phoneNumber && (
            <div style={{ fontSize: "0.8rem", color: "#6b7280", marginTop: "4px", marginLeft: "28px" }}>
              * Enter a phone number to enable notifications
            </div>
          )}
        </div>
        {/* --------------------------------- */}

        <button
          type="button"
          className="add-link-btn"
          onClick={handleAddLink}
          disabled={!canAddMore}
          title={canAddMore ? "Add another link" : "Maximum 5 links"}
          style={{ marginTop: "0.75rem" }}
        >
          <FaPlus size={14} />
          <span>Add link</span>
        </button>

        {links.length > 0 && (
          <>
            <label style={{ marginTop: "0.75rem" }}>Links (up to 5)</label>
            <div className="links-list">
              {links.map((val, i) => {
                const filled = val.trim().length > 0;
                return (
                  <div className="links-row" key={i}>
                    <input
                      className="link-input"
                      placeholder="https://example.com/anything"
                      value={val}
                      onChange={(e) => handleChangeLink(i, e.target.value)}
                      inputMode="url"
                    />
                    <button
                      type="button"
                      className="remove-link-btn"
                      onClick={() => handleRemoveLink(i)}
                      title="Remove this link"
                      disabled={!filled && links.length === 1}
                    >
                      <FaTrash size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <button onClick={handleSave} className="save-button" style={{ marginTop: "0.75rem" }}>
          Update profile
        </button>

        {message && <p style={{ marginTop: "1rem", color: "#53FF8A" }}>{message}</p>}
      </div>

      <div className="djdel-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>Delete account</div>
            {deletionInfo?.status === "pending_delete" ? (
              <div style={{ color: "#fbbf24", fontSize: "0.78rem", marginTop: 4, lineHeight: 1.4 }}>
                Scheduled for deletion on{" "}
                <strong>{deletionInfo?.scheduled ? new Date(deletionInfo.scheduled).toLocaleString() : "—"}</strong>.
                Log in before this date to cancel.
              </div>
            ) : (
              <div style={{ color: "#6b7280", fontSize: "0.78rem", marginTop: 4, lineHeight: 1.4 }}>
                Permanently delete after a 7-day grace period.
              </div>
            )}
          </div>

          {deletionInfo?.status === "pending_delete" ? (
            <button
              onClick={cancelAccountDeletion}
              disabled={delBusy}
              style={{
                background: "rgba(37, 99, 235, 0.12)", color: "#60a5fa", border: "1px solid rgba(37, 99, 235, 0.25)",
                borderRadius: 10, padding: "8px 14px", cursor: "pointer", fontWeight: 600, fontSize: "0.82rem",
                whiteSpace: "nowrap", flexShrink: 0,
              }}
            >
              Cancel
            </button>
          ) : (
            <button
              onClick={() => setDelOpen(true)}
              disabled={delBusy}
              style={{
                background: "rgba(239, 68, 68, 0.1)", color: "#ef4444", border: "1px solid rgba(239, 68, 68, 0.2)",
                borderRadius: 10, padding: "8px 14px", cursor: "pointer", fontWeight: 600, fontSize: "0.82rem",
                whiteSpace: "nowrap", flexShrink: 0,
              }}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {showPhonePrompt && (
        <PhonePromptModal
          onSaved={(fullPhone) => {
            setPhoneNumber(fullPhone);
            setIsSmsEnabled(true);
            setShowPhonePrompt(false);
            setMessage("Phone number saved and notifications enabled!");
          }}
          onDismiss={() => setShowPhonePrompt(false)}
        />
      )}

      {delOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => !delBusy && setDelOpen(false)}
        >
          <div
            style={{
              background: "#111827",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 16,
              padding: 22,
              width: "min(92vw, 380px)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 800, fontSize: "1rem", marginBottom: 8 }}>
              Confirm deletion
            </div>
            <div style={{ color: "#9ca3af", fontSize: 13, marginBottom: 18, lineHeight: 1.5 }}>
              Your account will be permanently removed in <b style={{ color: "#fff" }}>7 days</b>.
              Log in anytime within this period to cancel.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={() => setDelOpen(false)}
                disabled={delBusy}
                style={{
                  padding: "9px 16px",
                  borderRadius: 10,
                  background: "rgba(255,255,255,0.06)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.08)",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                }}
              >
                Cancel
              </button>
              <button
                onClick={requestAccountDeletion}
                disabled={delBusy}
                style={{
                  padding: "9px 16px",
                  borderRadius: 10,
                  background: "#ef4444",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                }}
              >
                {delBusy ? "Scheduling\u2026" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;